import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, CircularProgress, Box, Typography, IconButton, FormControlLabel, Switch, Paper, Tabs, Tab
} from '@mui/material';
import { Dns, Close, InfoOutlined, Code, Visibility } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useDeviceTemplates } from '../../hooks';
import { THEME_COLORS, OPERATION_STATUS, UX_TIMING } from '../../constants';
import ConfirmationModal from './ConfirmationModal';
import TemplateVariablesPicker from './TemplateVariablesPicker';
import templateVariableService from '../../services/templateVariableService';

// Componente de información sobre el comportamiento de las plantillas
const TemplateInfo = () => (
  <Paper variant="outlined" sx={{
    p: 3,
    backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
    borderRadius: '12px',
    border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
    mb: 2
  }}>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <InfoOutlined sx={{ color: THEME_COLORS.TEXT_SECONDARY, mt: 0.5 }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, mb: 1 }}>
          💡 Comportamiento de las Plantillas
        </Typography>
        <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY, lineHeight: 1.5 }}>
          • Las plantillas vacías se desactivan automáticamente<br />
          • Puedes desactivar plantillas con contenido (se conserva el texto para reactivar después)<br />
          • Solo las plantillas activas enviarán notificaciones de webhook<br />
          • Las variables se reemplazan automáticamente al enviar mensajes
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const DeviceTemplateModal = ({ open, onClose, device, onSubmit }) => {
  const { templates, loading, loadTemplates, updateTemplate, createTemplate } = useDeviceTemplates(device?.device_id);

  // Referencias a los textareas para insertar variables
  const switchOnTextareaRef = useRef(null);
  const switchOffTextareaRef = useRef(null);

  // Estado para switches (ON/OFF)
  const [formData, setFormData] = useState({
    switch_on: '',
    switch_on_active: false,
    switch_off: '',
    switch_off_active: false,
  });
  // Estado para radiofrecuencia: lista de plantillas rfTrigN
  const [rfTemplates, setRfTemplates] = useState([]);

  // DEBUG: Log de rfTemplates cada vez que cambian
  useEffect(() => {
    if (device?.device_type === 'radio_frecuencia') {
      console.log('[DEBUG] rfTemplates actualizado:', rfTemplates);
    }
  }, [rfTemplates, device]);
  // Estado para manejar las tabs
  const [activeTab, setActiveTab] = useState(0);

  // Estados para modal de operación
  const [operationStatus, setOperationStatus] = useState(OPERATION_STATUS.IDLE);
  const [showOperationModal, setShowOperationModal] = useState(false);
  const [operationError, setOperationError] = useState(null);

  useEffect(() => {
    if (device?.device_id) {
      loadTemplates();
    }
  }, [device, loadTemplates]);

  useEffect(() => {
    if (device?.device_type === 'radio_frecuencia') {
      // Filtrar y ordenar plantillas rfTrigN
      const rfTemplatesSorted = templates
        .filter(t => /^rfTrig\d+$/.test(t.event_key))
        .sort((a, b) => {
          const nA = parseInt(a.event_key.replace('rfTrig', ''));
          const nB = parseInt(b.event_key.replace('rfTrig', ''));
          return nA - nB;
        })
        .map(t => ({
          ...t,
          is_active: !!t.is_active // Fuerza a booleano
        }));
      setRfTemplates(rfTemplatesSorted);
    } else {
      // Lógica para switches
      const onTemplate = templates.find(t => t.event_key === 'switch_on');
      const offTemplate = templates.find(t => t.event_key === 'switch_off');
      setFormData({
        switch_on: onTemplate?.message_body || '',
        switch_on_active: onTemplate ? !!onTemplate.is_active : false,
        switch_off: offTemplate?.message_body || '',
        switch_off_active: offTemplate ? !!offTemplate.is_active : false,
      });
    }
  }, [templates, device]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleRfInputChange = (idx, e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;
    if (name === 'is_active') {
      newValue = !!checked; // Fuerza a booleano SIEMPRE
      console.log(`[DEBUG] Switch de activación RF idx=${idx} antes:`, rfTemplates[idx]);
      console.log(`[DEBUG] Cambiando is_active a:`, newValue);
    }
    setRfTemplates(prev => prev.map((tpl, i) => i === idx ? { ...tpl, [name]: newValue } : tpl));
  };

  const handleAddRfTemplate = async () => {
    const nextIdx = rfTemplates.length;
    const event_key = `rfTrig${nextIdx}`;
    const event_name = `Sensor ${nextIdx + 1}`;
    // Crear plantilla en backend y recargar
    await createTemplate({
      event_key,
      event_name,
      message_body: `Mensaje para cuando el ${event_name.toLowerCase()} se activa`,
      is_active: false // Asegura booleano
    });
    await loadTemplates();
  };

  const handleSubmit = async () => {

    // Mostrar modal de operación
    setOperationStatus(OPERATION_STATUS.LOADING);
    setShowOperationModal(true);
    setOperationError(null);

    const startTime = Date.now();

    try {
      const promises = [];
      if (device?.device_type === 'radio_frecuencia') {
        // DEBUG: Log de rfTemplates al guardar
        console.log('[DEBUG] rfTemplates al guardar:', rfTemplates);
        // Guardar todas las plantillas rfTrigN
        for (const tpl of rfTemplates) {
          const original = templates.find(t => t.template_id === tpl.template_id);
          const dataChanged = tpl.message_body !== (original?.message_body || '') || tpl.is_active !== (original ? !!original.is_active : false);
          if (dataChanged) {
            const payload = {
              event_name: tpl.event_name,
              message_body: tpl.message_body,
              is_active: tpl.message_body.trim() === '' ? false : tpl.is_active
            };
            // DEBUG: Log de payload enviado al backend
            console.log(`[DEBUG] Actualizando plantilla RF id=${tpl.template_id}:`, payload);
            promises.push(updateTemplate(tpl.template_id, payload));
          }
        }
      } else {
        // Lógica para switches
        const originalOnTemplate = templates.find(t => t.event_key === 'switch_on');
        const originalOffTemplate = templates.find(t => t.event_key === 'switch_off');
        const onDataChanged = formData.switch_on !== (originalOnTemplate?.message_body || '') || formData.switch_on_active !== (originalOnTemplate ? !!originalOnTemplate.is_active : false);
        const offDataChanged = formData.switch_off !== (originalOffTemplate?.message_body || '') || formData.switch_off_active !== (originalOffTemplate ? !!originalOffTemplate.is_active : false);
        if (onDataChanged) {
          const finalSwitchOnActive = formData.switch_on.trim() === '' ? false : formData.switch_on_active;
          const payload = {
            event_name: 'Encendido',
            message_body: formData.switch_on,
            is_active: finalSwitchOnActive
          };
          if (originalOnTemplate) {
            promises.push(updateTemplate(originalOnTemplate.template_id, payload));
          } else {
            promises.push(createTemplate({ ...payload, event_key: 'switch_on' }));
          }
        }
        if (offDataChanged) {
          const finalSwitchOffActive = formData.switch_off.trim() === '' ? false : formData.switch_off_active;
          const payload = {
            event_name: 'Apagado',
            message_body: formData.switch_off,
            is_active: finalSwitchOffActive
          };
          if (originalOffTemplate) {
            promises.push(updateTemplate(originalOffTemplate.template_id, payload));
          } else {
            promises.push(createTemplate({ ...payload, event_key: 'switch_off' }));
          }
        }
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      // Garantizar 1.5s mínimo de UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      setOperationStatus(OPERATION_STATUS.SUCCESS);

      // Mostrar éxito por 2 segundos
      setTimeout(() => {
        setShowOperationModal(false);
        setOperationStatus(OPERATION_STATUS.IDLE);

        // Cerrar modal
        onClose();
      }, UX_TIMING.SUCCESS_DISPLAY_TIME);

    } catch (error) {
      console.error('❌ ERROR en handleSubmit:', error);
      console.error('   Mensaje del error:', error.message);
      console.error('   Stack trace:', error.stack);

      setOperationStatus(OPERATION_STATUS.ERROR);
      setOperationError(error.message || 'Error al guardar plantillas');

      // Mostrar error por 3 segundos
      setTimeout(() => {
        setShowOperationModal(false);
        setOperationStatus(OPERATION_STATUS.IDLE);
      }, UX_TIMING.ERROR_DISPLAY_TIME);
    }
  };

  const handleClose = () => {
    setOperationStatus(OPERATION_STATUS.IDLE);
    setShowOperationModal(false);
    setOperationError(null);
    setActiveTab(0); // Resetear a la primera tab
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleVariableSelect = (variable) => {
    // Determinar cuál textarea está activo y insertar la variable
    if (activeTab === 0) {
      // Si estamos en la tab de plantillas, insertar en el textarea que tenga focus
      const activeElement = document.activeElement;

      if (switchOnTextareaRef.current && activeElement === switchOnTextareaRef.current) {
        templateVariableService.insertVariableInTextarea(variable, switchOnTextareaRef.current);
      } else if (switchOffTextareaRef.current && activeElement === switchOffTextareaRef.current) {
        templateVariableService.insertVariableInTextarea(variable, switchOffTextareaRef.current);
      } else {
        // Si ninguno tiene focus, insertar en el último utilizado o el primero por defecto
        templateVariableService.insertVariableInTextarea(variable, switchOnTextareaRef.current);
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
            backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
            boxShadow: `0 8px 32px ${THEME_COLORS.SHADOW_LIGHT}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT, borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`, p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: THEME_COLORS.TEXT_PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Dns sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '1.25rem' }}>
                  Configurar Plantillas
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Editando para: {device?.nombre}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleClose}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 4, paddingTop: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>
              {/* Información sobre comportamiento */}
              <Box sx={{ p: 3, pb: 0 }}>
                <TemplateInfo />
              </Box>

              {/* Un solo set de tabs para ambos tipos */}
              <Box>
                <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                  <Tab icon={<Code sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />} label={device?.device_type === 'radio_frecuencia' ? 'Editar Plantilla' : 'Editar Plantillas'} sx={{ minHeight: 48, color: THEME_COLORS.TEXT_SECONDARY, '&.Mui-selected': { color: THEME_COLORS.TEXT_PRIMARY } }} />
                  <Tab icon={<Visibility sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />} label="Variables Disponibles" sx={{ minHeight: 48, color: THEME_COLORS.TEXT_SECONDARY, '&.Mui-selected': { color: THEME_COLORS.TEXT_PRIMARY } }} />
                </Tabs>
                {activeTab === 0 && (
                  <Box sx={{ p: 3 }}>
                    {device?.device_type === 'radio_frecuencia' ? (
                      <Box>
                        {rfTemplates.map((tpl, idx) => (
                          <Paper key={tpl.event_key} variant="outlined" sx={{ p: 3, borderRadius: '12px', border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`, mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '1.1rem', mb: 2 }}>
                              Plantilla para {tpl.event_name} (RF)
                            </Typography>
                            <TextField
                              name="message_body"
                              label={`Mensaje para cuando el ${tpl.event_name} se activa`}
                              value={tpl.message_body || ''}
                              onChange={e => handleRfInputChange(idx, { ...e, target: { ...e.target, name: 'message_body' } })}
                              fullWidth
                              multiline
                              rows={4}
                              placeholder={`Ej: El ${tpl.event_name.toLowerCase()} del dispositivo {{dispositivo_nombre}} se ha activado el {{fecha}} a las {{hora}}`}
                              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: THEME_COLORS.BACKGROUND_PAPER, '& textarea': { fontFamily: 'monospace', fontSize: '0.9rem' } } }}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={!!tpl.is_active}
                                  onChange={e => handleRfInputChange(idx, { ...e, target: { ...e.target, name: 'is_active' } })}
                                  name="is_active"
                                  disabled={false}
                                />
                              }
                              label={
                                (tpl.message_body || '').trim() === ''
                                  ? "Plantilla inactiva (vacía)"
                                  : tpl.is_active
                                    ? "Plantilla activa"
                                    : "Plantilla inactiva (con contenido)"
                              }
                              sx={{ color: (tpl.message_body || '').trim() === '' || !tpl.is_active ? THEME_COLORS.TEXT_SECONDARY : THEME_COLORS.TEXT_PRIMARY }}
                            />
                          </Paper>
                        ))}
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleAddRfTemplate}
                          sx={{ borderRadius: '12px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 500, minWidth: 180 }}
                        >
                          Agregar plantilla para otro sensor
                        </Button>
                      </Box>
                    ) : (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 3, borderRadius: '12px', border: `1px solid ${THEME_COLORS.BORDER_LIGHT}` }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '1.1rem', mb: 2 }}>
                              Plantilla para Evento ON (Encendido)
                            </Typography>
                            <TextField
                              inputRef={switchOnTextareaRef}
                              name="switch_on"
                              label="Mensaje para cuando el dispositivo se enciende"
                              value={formData.switch_on}
                              onChange={handleInputChange}
                              fullWidth
                              multiline
                              rows={4}
                              placeholder="Ej: El dispositivo {{dispositivo_nombre}} en {{dispositivo_ubicacion}} se ha ENCENDIDO el {{fecha}} a las {{hora}}"
                              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: THEME_COLORS.BACKGROUND_PAPER, '& textarea': { fontFamily: 'monospace', fontSize: '0.9rem' } } }}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={formData.switch_on_active}
                                  onChange={handleInputChange}
                                  name="switch_on_active"
                                  disabled={false}
                                />
                              }
                              label={
                                formData.switch_on.trim() === ''
                                  ? "Plantilla inactiva (vacía)"
                                  : formData.switch_on_active
                                    ? "Plantilla activa"
                                    : "Plantilla inactiva (con contenido)"
                              }
                              sx={{ color: formData.switch_on.trim() === '' || !formData.switch_on_active ? THEME_COLORS.TEXT_SECONDARY : THEME_COLORS.TEXT_PRIMARY }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 3, borderRadius: '12px', border: `1px solid ${THEME_COLORS.BORDER_LIGHT}` }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '1.1rem', mb: 2 }}>
                              Plantilla para Evento OFF (Apagado)
                            </Typography>
                            <TextField
                              inputRef={switchOffTextareaRef}
                              name="switch_off"
                              label="Mensaje para cuando el dispositivo se apaga"
                              value={formData.switch_off}
                              onChange={handleInputChange}
                              fullWidth
                              multiline
                              rows={4}
                              placeholder="Ej: El dispositivo {{dispositivo_nombre}} en {{dispositivo_ubicacion}} se ha APAGADO el {{fecha}} a las {{hora}}"
                              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: THEME_COLORS.BACKGROUND_PAPER, '& textarea': { fontFamily: 'monospace', fontSize: '0.9rem' } } }}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={formData.switch_off_active}
                                  onChange={handleInputChange}
                                  name="switch_off_active"
                                  disabled={false}
                                />
                              }
                              label={
                                formData.switch_off.trim() === ''
                                  ? "Plantilla inactiva (vacía)"
                                  : formData.switch_off_active
                                    ? "Plantilla activa"
                                    : "Plantilla inactiva (con contenido)"
                              }
                              sx={{ color: formData.switch_off.trim() === '' || !formData.switch_off_active ? THEME_COLORS.TEXT_SECONDARY : THEME_COLORS.TEXT_PRIMARY }}
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                )}
                {activeTab === 1 && (
                  <Box sx={{ p: 3 }}>
                    <TemplateVariablesPicker onVariableSelect={handleVariableSelect} deviceId={device?.device_id} />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT, borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`, p: 3, gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} variant="outlined" size="large" sx={{ color: THEME_COLORS.TEXT_SECONDARY, borderColor: THEME_COLORS.BORDER_LIGHT, borderRadius: '12px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 500, minWidth: 120 }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ backgroundColor: THEME_COLORS.TEXT_PRIMARY, color: THEME_COLORS.BACKGROUND_PAPER, borderRadius: '12px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 600, minWidth: 140, '&:hover': { backgroundColor: '#1a1a1a' } }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Operación */}
      <ConfirmationModal
        open={showOperationModal}
        status={operationStatus}
        title={
          operationStatus === OPERATION_STATUS.LOADING
            ? 'Guardando Plantillas'
            : operationStatus === OPERATION_STATUS.SUCCESS
              ? '¡Plantillas Guardadas!'
              : 'Error'
        }
        subtitle={
          operationStatus === OPERATION_STATUS.LOADING
            ? 'Por favor espera mientras guardamos las plantillas...'
            : operationStatus === OPERATION_STATUS.SUCCESS
              ? `Plantillas configuradas exitosamente para ${device?.nombre}`
              : operationError || 'Hubo un problema al guardar las plantillas'
        }
        userName={device?.nombre || ''}
        hideActions={true}
      />
    </>
  );
};

export default DeviceTemplateModal; 